import './access-denied.scss';

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { isObserverAdminRoles } from '@/authz/authority';
import { scopeRoles, useDelegation } from '@/authz/delegation';
import { describeDestination } from '@/authz/menu-visibility';
import { TypeButton } from '@/components/reusable/button';
import { showToast } from '@/components/reusable/toast';
import { APPUSERROLES, getUserData } from '@/constants/global';
import { pageurl } from '@/constants/pageurl';
import { useUsersInRoleQuery } from '@/hooks/queries/management/use-role-queries';
import { menuData } from '@/layout/menu/components/data';
import { humanizeActionName } from '@/right-panel/role/action-categories';

import { breadcrumb, COPY, headline, lede, requestMessage, teamGrantHint } from './copy';

interface IGranter {
	name: string;
	email: string;
}

/** The backend serializes unset fields as the literal string 'null'. */
const clean = (value: unknown): string => {
	const text = typeof value === 'string' ? value.trim() : '';
	return text === 'null' || text === 'undefined' ? '' : text;
};

/**
 * `useUsersInRoleQuery` had no consumers before this page, so its response shape is
 * unverified at runtime. Accept both the bare array and the standard envelope, and drop
 * anyone without a usable email rather than rendering a dead mailto: link.
 */
const readGranters = (payload: unknown): IGranter[] => {
	const rows = Array.isArray(payload)
		? payload
		: Array.isArray((payload as { data?: unknown })?.data)
			? ((payload as { data: unknown[] }).data ?? [])
			: [];

	return rows
		.map((row) => {
			const record = row as Record<string, unknown>;
			const first = clean(record.firstName);
			const last = clean(record.lastName);
			const name = clean(record.fullName) || [first, last].filter(Boolean).join(' ');
			return { name, email: clean(record.email) };
		})
		.filter((granter) => granter.email.length > 0)
		.map((granter) => ({ ...granter, name: granter.name || granter.email }));
};

interface AccessDeniedProps {
	/** Codes that would have opened the page. */
	missing?: readonly string[];
	/** Path the user tried to open. Defaults to the current location. */
	attemptedPath?: string;
}

/**
 * Rendered in place of a page the current user cannot open.
 *
 * Deliberately NOT a redirect to a /admin/403 route: the attempted URL is the thing the
 * user copies back to whoever sent them the link, `Back` keeps working, and a refresh
 * reproduces the problem. A 403 you can't reproduce is a 403 nobody can help with.
 */
const AccessDenied = ({ missing = [], attemptedPath }: AccessDeniedProps) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [copied, setCopied] = useState(false);

	const path = attemptedPath ?? location.pathname;
	const destination = useMemo(() => describeDestination(path, menuData), [path]);

	const user = getUserData().user;
	const roles = useMemo(() => (Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []), [user?.role]);
	const email = clean(user?.Email);
	const fullName = clean(user?.FullName) || 'Your account';

	const required = missing.length > 0 ? missing : destination.requiredActions;

	const delegation = useDelegation();
	const teamOwner = delegation.isTeamMember ? delegation.ownerName || 'the account owner' : null;

	const copyInput = {
		label: destination.label,
		trail: destination.trail,
		missing: required,
		roles,
		isKnown: destination.isKnown,
		teamOwner,
	};

	const granters = useUsersInRoleQuery(APPUSERROLES.SUPERADMIN);
	const granterList = useMemo(() => readGranters(granters.data), [granters.data]);

	useEffect(() => {
		document.title = `No access — ${destination.label}`;
	}, [destination.label]);

	const message = requestMessage({ ...copyInput, email });

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(message);
			setCopied(true);
			showToast('success', COPY.copied);
		} catch {
			showToast('error', 'Copying failed. Select the message and copy it manually.');
		}
	};

	// Observer Admins land on their dashboard, Party Admins and Candidates on My Election,
	// everyone else on Overview.
	// A team member's home is their owner's (scope, not access).
	const homeRoles = scopeRoles(roles);
	const homeIsMyElection = homeRoles.some((role) => role === APPUSERROLES.PARTYADMIN || role === APPUSERROLES.CANDIDATE);
	const home = isObserverAdminRoles(homeRoles)
		? pageurl.OBSERVER_DASHBOARD
		: homeIsMyElection
			? pageurl.MYELECTION
			: pageurl.OVERVIEW;

	return (
		<main className='access-denied'>
			<div className='access-denied__head'>
				{destination.isKnown && <p className='access-denied__eyebrow'>{breadcrumb(destination)}</p>}
				<h1 className='access-denied__title'>{headline(copyInput)}</h1>
				<p className='access-denied__lede'>{lede(copyInput)}</p>
			</div>

			<div className='access-denied__facts'>
				<section className='access-denied__fact'>
					<h2>{COPY.whatBlock}</h2>
					{destination.isKnown && <p className='access-denied__strong'>{breadcrumb(destination)}</p>}
					<p className='access-denied__code'>{path}</p>
				</section>

				<section className='access-denied__fact'>
					<h2>{COPY.whoBlock}</h2>
					<p className='access-denied__strong'>
						{fullName}
						{email && <span className='access-denied__muted'> · {email}</span>}
					</p>
					{roles.length > 0 ? (
						<ul className='access-denied__chips'>
							{roles.map((role) => (
								<li key={role}>{role}</li>
							))}
						</ul>
					) : (
						<p className='access-denied__muted'>{COPY.noRoles}</p>
					)}
				</section>

				<section className='access-denied__fact access-denied__fact--missing'>
					<h2>{COPY.missingBlock}</h2>
					{required.length === 0 ? (
						<p className='access-denied__muted'>This page needs a permission that isn’t recorded in the app.</p>
					) : (
						<>
							{required.length > 1 && <p className='access-denied__muted'>{COPY.multiMissingHint}</p>}
							<ul className='access-denied__perms'>
								{required.map((code) => (
									<li key={code}>
										<span className='access-denied__strong'>{humanizeActionName(code)}</span>
										<span className='access-denied__code'>{code}</span>
									</li>
								))}
							</ul>
						</>
					)}
				</section>

				<section className='access-denied__fact'>
					<h2>{COPY.grantBlock}</h2>
					<p className='access-denied__muted'>{teamOwner ? teamGrantHint(teamOwner) : COPY.grantHint}</p>
					{granters.isError || (!granters.isLoading && granterList.length === 0) ? (
						<p className='access-denied__muted'>{COPY.grantUnavailable}</p>
					) : (
						<ul className='access-denied__granters'>
							{granterList.slice(0, 4).map((granter) => (
								<li key={granter.email}>
									<span>{granter.name}</span>
									<a
										href={`mailto:${granter.email}?subject=${encodeURIComponent('iElect access request')}&body=${encodeURIComponent(message)}`}
									>
										Email
									</a>
								</li>
							))}
							{granterList.length > 4 && <li className='access-denied__muted'>+ {granterList.length - 4} more</li>}
						</ul>
					)}
				</section>
			</div>

			<div className='access-denied__ask'>
				<pre className='access-denied__message'>{message}</pre>
				<div className='access-denied__actions'>
					<TypeButton
						title={copied ? COPY.copied : COPY.copyAction}
						onClick={() => void onCopy()}
						buttonSize='small'
					/>
					<TypeButton
						title='Go back'
						buttonType='outlined'
						buttonSize='small'
						onClick={() => navigate(home)}
					/>
				</div>
				<p className='access-denied__muted'>{COPY.copyHint}</p>
			</div>

			<p
				className='access-denied__live'
				aria-live='polite'
			>
				{copied ? COPY.copied : ''}
			</p>
		</main>
	);
};

export default AccessDenied;
